import { useContext, useEffect, useMemo, useRef } from 'react'

import { serialiseOccupancy } from 'lib/search/occupancy'
import useQueryParams from 'hooks/useQueryParams'
import { useAppSelector } from 'hooks/reduxHooks'
import { reportLog } from 'services/userMonitoring'
import { getGhaPriceMatch, getGhaPriceMatchForProperty } from 'selectors/ghaPriceMatchSelectors'
import { getPropertyMetasearchPriceAccuracyCandidateRates } from 'selectors/offerPage/offerPageSelectors'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getGapToGooglePct, pickGoogleRoomRate } from 'lib/metasearch/ghaPriceGap'

interface Params {
  enabled: boolean
  found: boolean
  price: number | undefined
  rateId: string | undefined
  calculationMethod: string | undefined
  expectedRateId: string | undefined
  propertyId: string | undefined
  checkIn: string | undefined
  checkOut: string | undefined
  rooms: Array<OfferPageRoom>
  missingReason?: string
}

export default function useLogMetasearchInterestedOption(params: Params) {
  const {
    enabled,
    found,
    price,
    rateId,
    calculationMethod,
    expectedRateId,
    propertyId,
    checkIn,
    checkOut,
    rooms,
    missingReason,
  } = params
  const queryParams = useQueryParams()
  const userAgent = useAppSelector(state => state.system.rawUserAgentString)
  const region = useAppSelector(state => state.geo.currentRegionCode)
  const ghaPriceMatch = useAppSelector(state => !!propertyId && !!getGhaPriceMatchForProperty(state, propertyId))
  const occupancy = useMemo(() => serialiseOccupancy(rooms), [rooms])
  const offerPageState = useContext(OfferPageStateContext)
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const ghaClickThrough = useAppSelector(getGhaPriceMatch)
  // Only the property page has every room's rates in offer page state; the legacy bedbank page logs without them.
  const pageRates = useAppSelector(state => (
    offerPageState.property ? getPropertyMetasearchPriceAccuracyCandidateRates(offerPageState, state) : undefined
  ))
  const ghaLog = useMemo(() => {
    const googleRoomRate = ghaClickThrough && pageRates && pickGoogleRoomRate(
      pageRates.filter((rate): rate is App.EnrichedBedbankPropertyRate => rate.service.type === 'bedbank'),
      ghaClickThrough,
      currency,
      {
        roomId: rate => rate.service.roomId,
        refundable: rate => rate.headlineCancellationPolicyType === 'fullyRefundable',
        price: rate => rate.totals.displayPrice,
      },
    )
    return {
      currency,
      googlePrice: ghaClickThrough?.googlePrice,
      googleCurrency: ghaClickThrough?.googleCurrency,
      priceGapToGooglePct: price === undefined ? undefined : getGapToGooglePct(price, currency, ghaClickThrough),
      googleRoomRate: googleRoomRate && {
        rateId: googleRoomRate.service.rateId,
        price: googleRoomRate.totals.displayPrice,
        calculationMethod: googleRoomRate.service.pricingMethod,
        gapToGooglePct: getGapToGooglePct(googleRoomRate.totals.displayPrice, currency, ghaClickThrough),
        isShownRate: googleRoomRate.service.rateId === rateId,
      },
    }
  }, [currency, ghaClickThrough, pageRates, price, rateId])
  const queryParamsObject = useMemo(() => Object.fromEntries(queryParams.entries()), [queryParams])
  const isMetasearchTraffic = queryParams.get('source') === 'metasearch' || queryParams.get('utm_content') === 'gha-lpp' || queryParams.get('gha_price') === '1'
  const hasSelectedDates = !!checkIn && !!checkOut
  const logKey = useMemo(() => JSON.stringify({
    userAgent,
    region,
    found,
    price,
    rateId,
    calculationMethod,
    ghaPriceMatch,
    expectedRateId,
    propertyId,
    checkIn,
    checkOut,
    occupancy,
    queryParams: queryParamsObject,
    missingReason,
    ...ghaLog,
  }), [calculationMethod, checkIn, checkOut, expectedRateId, found, ghaLog, ghaPriceMatch, missingReason, occupancy, price, propertyId, queryParamsObject, rateId, region, userAgent])
  const loggedKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!enabled || !isMetasearchTraffic || !hasSelectedDates || loggedKeyRef.current === logKey) {
      return
    }

    if (!found) {
      loggedKeyRef.current = logKey
      reportLog('Metasearch interested option missing', {
        userAgent,
        region,
        expectedRateId,
        rateId,
        calculationMethod,
        ghaPriceMatch,
        propertyId,
        checkIn,
        checkOut,
        occupancy,
        missingReason,
        queryParams: queryParamsObject,
        ...ghaLog,
      }, 'warn')
      return
    }

    if (price === undefined) {
      return
    }

    loggedKeyRef.current = logKey
    reportLog('Metasearch interested option shown', {
      userAgent,
      region,
      price,
      rateId,
      calculationMethod,
      ghaPriceMatch,
      expectedRateId,
      propertyId,
      checkIn,
      checkOut,
      occupancy,
      queryParams: queryParamsObject,
      ...ghaLog,
    }, 'info')
  }, [calculationMethod, checkIn, checkOut, enabled, expectedRateId, found, ghaLog, ghaPriceMatch, hasSelectedDates, isMetasearchTraffic, logKey, missingReason, occupancy, price, propertyId, queryParamsObject, rateId, region, userAgent])
}
